import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Brand } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { api } from "@/lib/api";

const NEW_ACCOUNT_MS = 10 * 60 * 1000;

/** The signed-in view: the top bar and the list of databases, which is empty until creating databases arrives. */
export function DatabasesPage({ email, onSignOut }: { email: string; onSignOut: () => Promise<void> }) {
  const account = useQuery({ queryKey: ["account"], queryFn: api.getAccount });
  const [signingOut, setSigningOut] = useState(false);
  const isNew = account.data ? Date.now() - Date.parse(account.data.createdAt) < NEW_ACCOUNT_MS : false;

  return (
    <div className="flex min-h-svh flex-col bg-surface text-ink">
      <header className="flex min-h-[70px] flex-wrap items-center gap-x-[14px] gap-y-2 border-b border-line bg-surface-raised px-[max(20px,4vw)] py-2">
        <Brand className="grow" />
        <div className="flex min-w-0 items-center gap-3">
          <span className="truncate text-sm leading-tight font-semibold" data-testid="signed-in-email">
            {email}
          </span>
          <span
            aria-hidden="true"
            className="flex size-9 shrink-0 items-center justify-center rounded-pill bg-ink font-display text-sm font-bold text-surface-raised uppercase"
          >
            {email.charAt(0)}
          </span>
          <Button
            variant="ghost"
            size="inline"
            disabled={signingOut}
            onClick={async () => {
              setSigningOut(true);
              try {
                await onSignOut();
              } finally {
                setSigningOut(false);
              }
            }}
          >
            {signingOut ? "Signing out…" : "Sign out"}
          </Button>
        </div>
      </header>

      <main className="mx-auto box-border flex w-full max-w-[1320px] flex-col gap-[30px] px-[max(20px,4vw)] py-[60px]">
        <div className="flex flex-col gap-2">
          <h1 className="heading m-0 text-[32px]">Your databases</h1>
          <p className="m-0 text-base leading-[1.6] text-ink-muted">
            {isNew ? "Welcome. Your account is ready." : "You have no databases yet."}
          </p>
        </div>

        <Card glow className="flex flex-col items-center gap-5 px-[30px] py-[60px] text-center">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true" className="text-ink-muted">
            <ellipse cx="24" cy="12" rx="14" ry="5" />
            <path d="M10 12v12c0 2.8 6.3 5 14 5s14-2.2 14-5V12" />
            <path d="M10 24v12c0 2.8 6.3 5 14 5s14-2.2 14-5V24" />
          </svg>
          <div className="flex max-w-[30em] flex-col gap-2">
            <h2 className="heading m-0 text-2xl">Create your first database</h2>
            <p className="m-0 text-base leading-[1.6] text-ink-muted">
              A database will be ready in about 15 seconds, with a hostname and credentials to connect with exapump or any JDBC
              client.
            </p>
          </div>
          <div className="flex flex-col items-center gap-2">
            <Button size="lg" disabled aria-describedby="create-note">
              Create database
            </Button>
            <span id="create-note" className="text-sm leading-normal text-ink-muted">
              Creating databases comes next in this prototype.
            </span>
          </div>
        </Card>

        <div className="flex flex-col gap-1 text-sm leading-normal text-ink-muted">
          <p className="m-0">Every database has 4 vCPUs and 30 GB of memory and runs in eu-central-1.</p>
          {account.isError ? (
            <p role="alert" className="m-0 text-ink">
              Your account could not be loaded: {account.error.message}{" "}
              <Button variant="ghost" size="inline" onClick={() => void account.refetch()}>
                Try again
              </Button>
            </p>
          ) : (
            <p className="m-0">
              Account <span className="font-mono text-ink" data-testid="account-id">{account.data?.id ?? "…"}</span>
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
